import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { fetchLegalPage, listLegalPages, updateLegalPage } from '../api/adminApi';
import { getPublicSiteOrigin } from '../config/adminApi';

const TABS = [
  { slug: 'privacy-policy', label: 'Privacy Policy', path: '/privacy-policy' },
  { slug: 'terms-and-conditions', label: 'Terms & Conditions', path: '/terms-and-conditions' },
];

export default function LegalPages() {
  const publicOrigin = getPublicSiteOrigin();
  const [activeSlug, setActiveSlug] = useState(TABS[0].slug);
  const [pagesBySlug, setPagesBySlug] = useState({});
  const [title, setTitle] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const activeTab = TABS.find((t) => t.slug === activeSlug) ?? TABS[0];
  const publicUrl = `${publicOrigin}${activeTab.path}`;

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listLegalPages();
      const map = {};
      for (const item of Array.isArray(list) ? list : []) {
        if (item?.slug) map[item.slug] = item;
      }
      setPagesBySlug(map);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const doc = pagesBySlug[activeSlug];
    if (doc) {
      setTitle(doc.title ?? '');
      setMarkdown(doc.markdown ?? '');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const docOne = await fetchLegalPage(activeSlug);
        if (!cancelled && docOne) {
          setPagesBySlug((prev) => ({ ...prev, [activeSlug]: docOne }));
          setTitle(docOne.title ?? '');
          setMarkdown(docOne.markdown ?? '');
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSlug, pagesBySlug]);

  const isDirty = useMemo(() => {
    const doc = pagesBySlug[activeSlug];
    if (!doc) return Boolean(title || markdown);
    return (doc.title ?? '') !== title || (doc.markdown ?? '') !== markdown;
  }, [activeSlug, pagesBySlug, title, markdown]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const updated = await updateLegalPage(activeSlug, { title: title.trim(), markdown });
      setPagesBySlug((prev) => ({ ...prev, [activeSlug]: updated }));
      setTitle(updated.title ?? '');
      setMarkdown(updated.markdown ?? '');
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  function switchTab(slug) {
    const doc = pagesBySlug[slug];
    if (doc) {
      setTitle(doc.title ?? '');
      setMarkdown(doc.markdown ?? '');
    } else {
      setTitle('');
      setMarkdown('');
    }
    setActiveSlug(slug);
    setError('');
  }

  if (loading && !Object.keys(pagesBySlug).length) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="legal-pages-editor">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div>
          <h2 className="h6 mb-1">Legal pages</h2>
          <p className="small text-secondary mb-0">
            Edit markdown below. Published pages are live at{' '}
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              {publicUrl}
            </a>
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <ul className="nav nav-tabs mb-3">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.slug}>
            <button
              type="button"
              className={`nav-link ${activeSlug === tab.slug ? 'active' : ''}`}
              onClick={() => switchTab(tab.slug)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="mb-3">
        <label className="form-label small text-secondary" htmlFor="legal-page-title">
          Page title
        </label>
        <input
          id="legal-page-title"
          className="form-control"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="Privacy Policy"
        />
      </div>

      <div className="row g-3 legal-pages-editor__panes">
        <div className="col-lg-6">
          <label className="form-label small text-secondary" htmlFor="legal-page-markdown">
            Markdown
          </label>
          <textarea
            id="legal-page-markdown"
            className="form-control font-monospace legal-pages-editor__textarea"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            spellCheck={false}
            rows={22}
            placeholder="# Heading&#10;&#10;Write your policy here…"
          />
        </div>
        <div className="col-lg-6">
          <div className="small text-secondary mb-2">Preview</div>
          <div className="card legal-pages-editor__preview">
            <div className="card-body legal-pages-editor__preview-body">
              <ReactMarkdown>{markdown || '*Nothing to preview yet.*'}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
